/* Native Shopify/Liquid header: one desktop disclosure, one modal mobile drawer. */
class ShudhiHeader extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const on = (target, event, callback, options = {}) => target?.addEventListener(event, callback, { ...options, signal: this.abort.signal });
    this.surface = this.querySelector('.shudhi-header__surface');
    this.row = this.querySelector('.shudhi-header__row');
    this.panels = this.querySelector('.shudhi-header__panels');
    this.triggers = [...this.querySelectorAll('[data-panel-trigger]')];
    this.contents = [...this.querySelectorAll('[data-panel-content]')];
    this.drawer = this.querySelector('dialog');
    this.drawerTrigger = this.querySelector('[data-drawer-open]');
    this.desktop = matchMedia('(min-width: 1024px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.active = null;
    this.suppressFocus = false;
    this.dataset.overHero = String(this.dataset.home === 'true' && !!document.querySelector('main > .shopify-section:first-child .shudhi-hero'));
    document.documentElement.style.setProperty('--header-height', '80px');
    this.sync();
    this.observer = new IntersectionObserver(([entry]) => {
      this.dataset.compact = String(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      this.sync();
      this.measure();
    });
    this.observer.observe(this.querySelector('.shudhi-header__sentinel'));
    this.triggers.forEach((trigger) => {
      on(trigger, 'pointerenter', (event) => { if (event.pointerType === 'mouse') this.openPanel(trigger); });
      on(trigger, 'focus', () => { if (!this.suppressFocus && !this.pointerDown) this.openPanel(trigger); });
      // Pointer focus must not open then immediately toggle closed on the same click.
      on(trigger, 'pointerdown', () => { this.pointerDown = true; });
      on(trigger, 'click', () => this.active === trigger ? this.closePanel() : this.openPanel(trigger));
      on(trigger, 'keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.openPanel(trigger);
          this.activeContent()?.querySelector('a')?.focus();
        }
      });
    });
    on(document, 'pointerup', () => { this.pointerDown = false; });
    on(document, 'pointercancel', () => { this.pointerDown = false; });
    on(this.surface, 'pointerleave', (event) => { if (event.pointerType === 'mouse') this.scheduleClose(); });
    on(this.surface, 'pointerenter', () => this.cancelClose());
    on(this.panels, 'pointerenter', () => this.cancelClose());
    on(this.panels, 'focusin', () => this.cancelClose());
    this.querySelectorAll('.shudhi-header__nav a, .shudhi-header__brand, .header__icons').forEach((element) => {
      on(element, 'pointerenter', () => this.scheduleClose());
      on(element, 'focusin', () => this.scheduleClose());
    });
    on(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) this.closePanel(); });
    on(this.panels, 'click', (event) => { if (event.target.closest('a')) this.closePanel(); });
    on(document, 'keydown', (event) => {
      if (event.key !== 'Escape' || !this.active || this.drawer.open) return;
      event.preventDefault();
      const trigger = this.active;
      this.closePanel();
      this.suppressFocus = true;
      trigger.focus();
      this.suppressFocus = false;
    });
    on(document, 'pointerdown', (event) => { if (!this.contains(event.target)) this.closePanel(); });
    on(window, 'resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.measure(), 80);
    });
    // Font swaps and the 64 → 40 logo transition both move the parent buttons.
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.row);
    this.resizeObserver.observe(this.querySelector('.shudhi-header__brand'));
    this.triggers.forEach((trigger) => this.resizeObserver.observe(trigger));
    document.fonts?.ready.then(() => { if (this.isConnected) this.measure(); });
    on(this.desktop, 'change', () => {
      this.closePanel();
      if (this.desktop.matches && !this.hasAttribute('data-drawer-only')) this.closeDrawer(true);
    });
    on(this.drawerTrigger, 'click', () => this.openDrawer());
    on(this.querySelector('[data-drawer-close]'), 'click', () => this.closeDrawer());
    on(this.drawer, 'cancel', (event) => { event.preventDefault(); this.closeDrawer(); });
    on(this.drawer, 'click', (event) => {
      if (event.target === this.drawer) {
        const rect = this.drawer.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right) this.closeDrawer();
      }
      if (event.target.closest('a')) this.closeDrawer(true);
    });
    on(this.drawer, 'close', () => this.restoreScroll());
    // Account events cross the shadow boundary but do not bubble.
    on(this, 'open', (event) => {
      if (!event.target.matches?.('shopify-account')) return;
      this.closePanel();
      event.target.style.setProperty('--account-dialog-top', `${Math.max(0, event.target.getBoundingClientRect().bottom)}px`);
    }, { capture: true });
  }

  sync() {
    this.dataset.panel = String(!!this.active);
    this.dataset.transparent = String(this.dataset.overHero === 'true' && this.dataset.compact === 'false' && !this.active);
    this.panels.inert = !this.active;
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(trigger === this.active)));
  }

  activeContent() {
    return this.active && this.contents.find((content) => content.dataset.panelContent === this.active.dataset.panelTrigger);
  }

  openPanel(trigger) {
    if (!this.desktop.matches || this.hasAttribute('data-drawer-only')) return;
    this.cancelClose();
    this.active = trigger;
    this.contents.forEach((content) => {
      const selected = content === this.activeContent();
      content.hidden = !selected;
      content.inert = !selected;
    });
    this.measure();
    this.sync();
  }

  closePanel() {
    this.cancelClose();
    this.active = null;
    // Retain the last list for the closing animation, but remove every focus target.
    this.contents.forEach((content) => { content.inert = true; });
    this.sync();
  }

  cancelClose() { clearTimeout(this.closeTimer); }
  scheduleClose() { this.cancelClose(); this.closeTimer = setTimeout(() => this.closePanel(), 150); }

  measure() {
    if (!this.active) return;
    const container = this.querySelector('.shudhi-header__panel-container');
    const box = container.getBoundingClientRect();
    const inset = parseFloat(getComputedStyle(container).paddingLeft);
    const offset = Math.max(0, this.active.getBoundingClientRect().left - box.left - inset);
    // Keep long translated labels inside the viewport near the right edge.
    const content = this.activeContent();
    const available = box.width - inset * 2;
    const required = Math.min(content.getBoundingClientRect().width, available);
    content.style.setProperty('--submenu-offset', `${Math.min(offset, Math.max(0, available - required))}px`);
  }

  openDrawer() {
    if (!this.drawer || this.drawer.open) return;
    clearTimeout(this.drawerTimer);
    this.closePanel();
    this.savedOverflow = document.documentElement.style.overflow;
    this.savedBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    this.drawer.showModal();
    this.drawerTrigger.setAttribute('aria-expanded', 'true');
    // Establish the initial style before transitioning the native top-layer dialog.
    this.drawer.getBoundingClientRect();
    this.drawer.dataset.visible = 'true';
  }

  closeDrawer(immediate = false) {
    if (!this.drawer?.open) return;
    this.drawer.dataset.visible = 'false';
    clearTimeout(this.drawerTimer);
    const finish = () => {
      this.drawer.close();
      this.drawerTrigger?.setAttribute('aria-expanded', 'false');
      this.restoreScroll();
    };
    if (immediate || this.reducedMotion.matches) finish();
    else this.drawerTimer = setTimeout(finish, 200);
  }

  restoreScroll() {
    if (this.savedOverflow === undefined) return;
    document.documentElement.style.overflow = this.savedOverflow;
    document.body.style.overflow = this.savedBodyOverflow;
    this.savedOverflow = undefined;
  }

  disconnectedCallback() {
    this.abort.abort();
    this.observer.disconnect();
    this.resizeObserver.disconnect();
    this.cancelClose();
    clearTimeout(this.resizeTimer);
    clearTimeout(this.drawerTimer);
    this.closeDrawer(true);
    this.restoreScroll();
  }
}
if (!customElements.get('shudhi-header')) customElements.define('shudhi-header', ShudhiHeader);
